import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { setBookingWizardSnapshot } from "@/context/BookingWizardContext";
import BadgeChip from "@/components/BadgeChip";
import { PriceBreakdown } from "@/components/booking-bits";
import { toast } from "sonner";
import { BookingStatusPill, RecurringTag } from "@/components/booking-bits";
import { ArrowLeft, ArrowRight, BadgeCheck, Check, Loader2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

// Backlog D — Booking + payment creation is HARD-DISABLED during
// impersonation (NOT gated). The Confirm & Pay path creates a real
// booking AND commits to a payment provider on another person's
// account; "confirm dialog" is insufficient protection there. Rule:
// "not allowed while impersonating." Same pattern as the
// `StripeConnectCard` onboarding disable.
const BOOKING_IMPERSONATION_TIP =
  "Booking/payment unavailable while impersonating";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function StepDots({ step }) {
  return (
    <div className="mb-6 flex gap-2" data-testid="wizard-step-dots">
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className={cn(
            "h-2 flex-1 rounded-full",
            i <= step ? "bg-brand-orange" : "bg-secondary"
          )}
        />
      ))}
    </div>
  );
}

export default function BookingWizard() {
  const { user, profile, refreshMe } = useAuth();
  const { pricing, providerDisplayLabel: providerLabel } = useConfig();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [scheduleType, setScheduleType] = useState("one_time");
  const [serviceDate, setServiceDate] = useState("");
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("13:00");
  const [dowPicks, setDowPicks] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [loadingProv, setLoadingProv] = useState(false);
  const [createdBooking, setCreatedBooking] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Phase C — first-booking flag mirrors the server-side determination
  // in POST /api/bookings (customer has ZERO prior bookings NOT IN
  // {cancelled, failed_never_serviced}). We fetch it once on mount from
  // GET /api/bookings/mine so the wizard's step-4 preview matches what
  // the backend will actually persist. Default `true` (marketing
  // anchor) until the fetch resolves — the fetch is fast and the
  // preview is only shown in step 4 which requires user interaction
  // to reach.
  const [isFirstBooking, setIsFirstBooking] = useState(true);

  useEffect(() => {
    refreshMe();
    // Commit B — §3.4 provider-scoped. `booking.wizard_start`
    // fires once on wizard mount. `provider_id` is unset here
    // because the provider is selected in step 3; the event only
    // marks funnel entry.
    trackEvent("booking.wizard_start");
    // sensible default: 7 days out
    const d = new Date();
    d.setDate(d.getDate() + 7);
    setServiceDate(d.toISOString().slice(0, 10));
    // Phase C — determine first-booking status by counting the
    // customer's prior bookings that WOULD disqualify them. Mirror
    // the backend's server-side rule exactly (statuses NOT in
    // {cancelled, failed_never_serviced} count as prior). If the fetch
    // fails we stay in the default optimistic-first state (true) — the
    // authoritative determination still happens server-side at POST.
    (async () => {
      try {
        const { data } = await api.get("/bookings/mine", { params: { role: "customer" } });
        const disqualify = new Set(["cancelled", "failed_never_serviced"]);
        const priorCount = (data || []).filter(
          (b) => !disqualify.has(b.status)
        ).length;
        setIsFirstBooking(priorCount === 0);
      } catch {
        // Fall back to the optimistic default; server-side is authoritative.
      }
    })();
    // Mount-only bootstrap: `refreshMe` (from useAuth) is intentionally
    // called ONCE to hydrate the auth context on wizard entry. Adding
    // it to deps would re-fire on any auth-context churn (token
    // refresh, profile patch) and reset the 7-day default + first-
    // booking count mid-wizard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Phase 3 §4 — write the wizard snapshot to the module-scoped store
  // so `<CustomerTopNav>` (a sibling of <Outlet>, not a descendant)
  // can render the step progress indicator instead of the 4 nav links.
  // Clears the snapshot on unmount so Confirmation/Success/Cancel
  // restore the normal nav.
  useEffect(() => {
    setBookingWizardSnapshot({ step, totalSteps: 4 });
    // Commit B — §3.4. `booking.step` fires on every step
    // transition (including the mount landing on step 1) with a
    // step_num prop so the dashboard can compute per-step
    // drop-off. `provider_id` set once the customer picks a
    // provider in step 3.
    trackEvent("booking.step", {
      step_num: step,
      provider_id: selectedProvider?.id || null,
    });
    return () => setBookingWizardSnapshot(null);
  }, [step]);

  const missing = useMemo(() => {
    if (!profile) return ["loading"];
    const m = [];
    if (!profile.primary_phone) m.push("primary phone");
    if (!profile.address_line1) m.push("address");
    if (!profile.zip) m.push("zip");
    if (!(profile.dogs || []).length) m.push("at least one dog");
    return m;
  }, [profile]);

  const [sortByTier, setSortByTier] = useState(false);

  // Auto-load providers when reaching step 3
  useEffect(() => {
    if (step !== 3 || !profile?.zip) return;
    (async () => {
      setLoadingProv(true);
      try {
        const params = { zip: profile.zip };
        if (sortByTier) params.sort_by_tier_level = true;
        const { data } = await api.get("/providers/search", { params });
        setProviders(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      } finally {
        setLoadingProv(false);
      }
    })();
  }, [step, profile?.zip, sortByTier]);

  const toggleDow = (i) => {
    setDowPicks((s) => {
      const has = s.includes(i);
      if (has) return s.filter((x) => x !== i);
      const cap = scheduleType === "recurring_weekly" ? 1 : 2;
      if (s.length >= cap) return [...s.slice(1), i];
      return [...s, i];
    });
  };

  const canProceedSchedule = () => {
    if (!serviceDate) return false;
    if (start >= end) return false;
    if (scheduleType === "recurring_weekly" && dowPicks.length !== 1) return false;
    if (scheduleType === "recurring_twice_weekly" && dowPicks.length !== 2) return false;
    return true;
  };

  const handleCreateAndPay = async () => {
    setSubmitting(true);
    try {
      const payload = {
        provider_id: selectedProvider.id,
        schedule_type: scheduleType,
        service_date: serviceDate,
        preferred_window: { start, end },
      };
      if (scheduleType !== "one_time") {
        payload.recurrence_rule = {
          frequency: scheduleType === "recurring_weekly" ? "weekly" : "twice_weekly",
          days_of_week: dowPicks.sort(),
        };
      }
      const { data: created } = await api.post("/bookings", payload);
      setCreatedBooking(created);
      // Phase 3b — real Stripe Checkout. We create a Checkout Session
      // server-side then redirect the customer to Stripe-hosted payment.
      // On return, `/booking/success` polls payment-status to confirm.
      try {
        const { data: session } = await api.post(
          `/bookings/${created.id}/checkout-session`,
        );
        if (!session?.checkout_url) {
          throw new Error("Checkout session response missing checkout_url");
        }
        window.location.assign(session.checkout_url);
        return;
      } catch (e) {
        const detail = e?.response?.data;
        if (e?.response?.status === 409 && detail?.error_code === "provider_not_payout_ready") {
          toast.error(
            "This provider hasn't finished setting up payouts. Please choose another provider or try again later.",
          );
          // Back to provider selection.
          setStep(3);
          return;
        }
        toast.error(formatApiErrorDetail(detail?.detail || detail) || e.message);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.missing_fields) {
        toast.error(`Complete your profile first: ${detail.missing_fields.join(", ")}`);
      } else if (detail && typeof detail === "object" && detail.error_code === "provider_missing_tier") {
        toast.error("This provider can't be booked right now — admin must assign a tier first.");
      } else {
        toast.error(formatApiErrorDetail(detail) || e.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;
  return (
    <div className="bg-background">
      <main className="mx-auto max-w-3xl px-6 py-10 w-full" data-testid="booking-wizard">
        {missing.length > 0 && missing[0] !== "loading" && (
          <div
            className="mb-6 rounded-2xl border border-brand-orange/40 bg-brand-orange/10 p-4 text-sm text-pp-text"
            data-testid="book-incomplete-banner"
          >
            Finish setting up your profile to continue — missing: <b>{missing.join(", ")}</b>.{" "}
            <button
              onClick={() => navigate("/dashboard/customer")}
              className="font-semibold underline hover:text-brand-orange"
            >
              Go to dashboard
            </button>
          </div>
        )}
        <StepDots step={step} />

        {step === 1 && (
          <Card className="rounded-3xl border-border bg-pp-surface p-8 shadow-none" data-testid="wizard-step-1">
            <h2 className="font-heading text-2xl font-semibold text-pp-text">
              Confirm service details
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              We&apos;ll send a PooPartner here.
            </p>
            <div className="mt-5 rounded-2xl border border-border bg-muted/30 p-4">
              <p className="text-sm font-bold uppercase tracking-wider text-brand-orange">Service address</p>
              <p className="mt-1 text-base font-semibold text-pp-text">
                {profile?.address_line1}
                {profile?.address_line2 ? `, ${profile.address_line2}` : ""}
              </p>
              <p className="text-sm text-muted-foreground">
                {profile?.city}, {profile?.state} {profile?.zip}
              </p>
            </div>
            <p className="mt-5 text-sm font-bold uppercase tracking-wider text-brand-orange">Dogs</p>
            <div className="mt-2 space-y-2">
              {(profile?.dogs || []).map((d) => (
                <div key={d.id} className="flex items-center justify-between rounded-2xl border border-border p-3" data-testid={`wizard-dog-${d.id}`}>
                  <span className="text-sm font-semibold text-pp-text">
                    {d.name} <span className="font-normal text-muted-foreground">· {d.breed || "Breed unknown"}</span>
                  </span>
                  {d.securement_fee_applies && (
                    <span className="text-xs font-bold text-brand-orange">+ securement fee</span>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-8 flex justify-end">
              <Button
                disabled={missing.length > 0}
                className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
                onClick={() => setStep(2)}
                data-testid="wizard-next-1"
              >
                Next <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </Card>
        )}

        {step === 2 && (
          <Card className="rounded-3xl border-border bg-pp-surface p-8 shadow-none" data-testid="wizard-step-2">
            <h2 className="font-heading text-2xl font-semibold text-pp-text">Choose schedule</h2>
            <div className="mt-5 grid gap-2 sm:grid-cols-3">
              {[
                { v: "one_time", label: "One-time" },
                { v: "recurring_weekly", label: "Weekly" },
                { v: "recurring_twice_weekly", label: "Twice weekly" },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => { setScheduleType(o.v); setDowPicks([]); }}
                  className={cn(
                    "rounded-2xl border p-4 text-left transition-all",
                    scheduleType === o.v
                      ? "border-brand-orange bg-brand-orange/10"
                      : "border-border bg-pp-surface hover:border-brand-orange/40"
                  )}
                  data-testid={`wizard-schedule-${o.v}`}
                >
                  <p className="text-sm font-bold uppercase tracking-wider text-pp-text">{o.label}</p>
                </button>
              ))}
            </div>

            <div className="mt-5 grid gap-4 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <Label className="text-sm font-semibold text-pp-text">Service date</Label>
                <Input
                  type="date"
                  value={serviceDate}
                  min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                  max={new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10)}
                  onChange={(e) => setServiceDate(e.target.value)}
                  className="mt-2 h-12 rounded-full px-5"
                  data-testid="wizard-date"
                />
              </div>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-sm font-semibold text-pp-text">Window start</Label>
                <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} className="mt-2 h-12 rounded-full px-5" data-testid="wizard-start" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-pp-text">Window end</Label>
                <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-2 h-12 rounded-full px-5" data-testid="wizard-end" />
              </div>
            </div>

            {scheduleType !== "one_time" && (
              <div className="mt-5">
                <Label className="text-sm font-semibold text-pp-text">
                  {scheduleType === "recurring_weekly" ? "Recurring day" : "Two recurring days"}
                </Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {DOW.map((d, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => toggleDow(i)}
                      className={cn(
                        "rounded-full border px-4 py-2 text-sm font-semibold",
                        dowPicks.includes(i)
                          ? "border-brand-orange bg-brand-orange text-white"
                          : "border-border bg-pp-surface text-pp-text hover:border-brand-orange/40"
                      )}
                      data-testid={`wizard-dow-${i}`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex justify-between">
              <Button variant="ghost" onClick={() => setStep(1)} className="rounded-full">
                <ArrowLeft className="mr-1 h-4 w-4" /> Back
              </Button>
              <Button
                disabled={!canProceedSchedule()}
                className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover"
                onClick={() => setStep(3)}
                data-testid="wizard-next-2"
              >
                Next <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </Card>
        )}

        {step === 3 && (
          <Card className="rounded-3xl border-border bg-pp-surface p-8 shadow-none" data-testid="wizard-step-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="font-heading text-2xl font-semibold text-pp-text">Choose your {providerLabel}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{providerLabel}s serving {profile?.zip}.</p>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Sort
                </label>
                <select
                  value={sortByTier ? "tier" : "default"}
                  onChange={(e) => setSortByTier(e.target.value === "tier")}
                  className="pp-select ml-2 mt-1 h-9 rounded-full border px-3 text-sm"
                  data-testid="wizard-provider-sort-select"
                >
                  <option value="default">All {providerLabel}s (default)</option>
                  <option value="tier">Sort by tier level</option>
                </select>
              </div>
            </div>
            {loadingProv ? (
              <div className="py-12 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-brand-orange" /></div>
            ) : providers.length === 0 ? (
              <p className="mt-5 rounded-2xl bg-muted p-6 text-center text-sm text-muted-foreground">
                No {providerLabel}s in your area yet — drop us a note.
              </p>
            ) : (
              <div className="mt-5 space-y-3">
                {providers.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { setSelectedProvider(p); setStep(4); }}
                    className={cn(
                      "w-full rounded-2xl border bg-pp-surface p-5 text-left transition-all hover:border-brand-orange/60",
                      selectedProvider?.id === p.id ? "border-brand-orange" : "border-border"
                    )}
                    data-testid={`wizard-provider-${p.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-heading text-lg font-semibold text-pp-text">{p.business_name}</p>
                          {p.is_pooplucker_default && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-brand-orange px-2 py-0.5 text-xs font-bold text-white">
                              <BadgeCheck className="h-3 w-3" /> Verified
                            </span>
                          )}
                          {p.tier && p.tier.badge_id && p.tier.badge_id !== "none" && (
                            <BadgeChip badgeId={p.tier.badge_id} testId={`wizard-provider-${p.id}-tier-badge`} />
                          )}
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{p.description}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                        ${(p.pricing.per_dog_price || 0).toFixed(2)} / dog · {p.pricing.recurring_discount_pct || 0}% recurring
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Est.</p>
                        <p className="font-heading text-2xl font-bold text-brand-orange">${p.min_estimated_price.toFixed(2)}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-6 flex justify-between">
              <Button variant="ghost" onClick={() => setStep(2)} className="rounded-full">
                <ArrowLeft className="mr-1 h-4 w-4" /> Back
              </Button>
            </div>
          </Card>
        )}

        {step === 4 && selectedProvider && (
          <Card className="rounded-3xl border-border bg-pp-surface p-8 shadow-none" data-testid="wizard-step-4">
            <h2 className="font-heading text-2xl font-semibold text-pp-text">Review &amp; confirm</h2>
            <div className="mt-5 space-y-3 rounded-2xl bg-muted/30 p-5 text-sm">
              <p><b>{providerLabel}:</b> {selectedProvider.business_name}</p>
              <p><b>Date:</b> {serviceDate} · {start}–{end}</p>
              <p><b>Schedule:</b> {scheduleType.replace("_", " ")}{scheduleType !== "one_time" ? ` · ${dowPicks.map((i) => DOW[i]).join(", ")}` : ""}</p>
              <p><b>Dogs:</b> {(profile?.dogs || []).length}</p>
            </div>

            <h3 className="mt-6 font-heading text-lg font-semibold text-pp-text">Itemized pricing</h3>
            {(() => {
              // Compute the wizard preview using the same formula the backend
              // will write into pricing_snapshot. Pure per-dog refactor.
              const dogs = profile?.dogs || [];
              const dogCount = dogs.length;
              const unsecured = dogs.filter((d) => d.securement_fee_applies).length;
              const perDog = selectedProvider.pricing.per_dog_price || 0;
              const sec = pricing.securement_fee || 0;
              const subtotal = perDog * dogCount + sec * unsecured;
              const discPct = scheduleType !== "one_time" ? (selectedProvider.pricing.recurring_discount_pct || 0) : 0;
              const discAmt = subtotal * discPct / 100;
              // Phase C — First-Clean Fee preview. Mirror the backend
              // one-pass rule: fee applies only when this is the
              // customer's first qualifying booking AND the platform
              // config has NOT waived it. Anchor (`first_clean_fee_cents`)
              // is surfaced even when waived so the strikethrough line
              // renders. When not first-booking, the fee is entirely
              // absent from the preview.
              const fcAnchorCents = isFirstBooking
                ? (pricing.first_clean_fee_cents || 0)
                : 0;
              const fcWaived = isFirstBooking
                ? (pricing.first_clean_fee_waived !== false)
                : false;
              const fcChargeCents =
                isFirstBooking && !fcWaived && fcAnchorCents > 0
                  ? fcAnchorCents
                  : 0;
              const fcChargeDollars = fcChargeCents / 100;
              const total = subtotal - discAmt + fcChargeDollars;
              const snapshot = {
                per_dog_price: perDog,
                recurring_discount_pct: discPct,
                securement_fee_per_dog: sec,
                subtotal,
                recurring_discount_amount: discAmt,
                total,
                is_first_booking: isFirstBooking,
                first_clean_fee_cents: fcAnchorCents,
                first_clean_fee_waived: fcWaived,
                first_clean_fee_charge_cents: fcChargeCents,
              };
              return (
                <div className="mt-3" data-testid="wizard-step4-breakdown">
                  <PriceBreakdown
                    snapshot={snapshot}
                    dogCount={dogCount}
                    unsecuredCount={unsecured}
                  />
                </div>
              );
            })()}

            <div className="mt-6 flex justify-between">
              <Button variant="ghost" onClick={() => setStep(3)} className="rounded-full">
                <ArrowLeft className="mr-1 h-4 w-4" /> Back
              </Button>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* span wrapper lets the tooltip target a disabled
                        button (Radix won't fire on a truly disabled
                        element). Matches the StripeConnectCard pattern. */}
                    <span className="inline-block">
                      <Button
                        disabled={submitting || !!user?.is_impersonating}
                        aria-disabled={user?.is_impersonating || undefined}
                        onClick={handleCreateAndPay}
                        className="rounded-full bg-pp-orange text-pp-cta-text hover:bg-pp-orange-hover disabled:cursor-not-allowed disabled:bg-slate-400 disabled:text-white disabled:opacity-90"
                        data-testid="wizard-confirm-pay"
                      >
                        {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Processing…</> : <><Check className="mr-1 h-4 w-4" />Confirm &amp; Pay</>}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {user?.is_impersonating && (
                    <TooltipContent
                      side="top"
                      align="end"
                      data-testid="wizard-booking-impersonation-tip"
                    >
                      <p className="text-xs">{BOOKING_IMPERSONATION_TIP}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}

function Line({ label, amount, bold }) {
  return (
    <div className={cn("flex justify-between", bold && "border-t border-border pt-2 font-semibold text-pp-text")}>
      <span className={!bold ? "text-muted-foreground" : ""}>{label}</span>
      <span>{amount < 0 ? `−$${Math.abs(amount).toFixed(2)}` : `$${(amount || 0).toFixed(2)}`}</span>
    </div>
  );
}
